import { useState, useEffect } from 'react';
import { useChartColors } from '@/hooks/useChartColors';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { DollarSign, Clock, Users, TrendingUp, Info, AlertCircle, Building2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

interface BillingBasedSavingsWidgetProps {
  agencyId?: string | null;
  agencyName?: string | null;
  startDate?: Date;
  endDate?: Date;
}

interface AgencyBillingData {
  state_billing_rate: number | null;
  caregiver_rate: number | null;
  patient_care_hours: number | null;
  hospital_visit_cost: number | null;
}

interface SavingsData {
  totalSessions: number;
  homeCases: number;
  pcpCases: number;
  urgentCareCases: number;
  erCases: number;
}

export default function BillingBasedSavingsWidget({ agencyId, agencyName, startDate, endDate }: BillingBasedSavingsWidgetProps) {
  const chartColors = useChartColors();
  const isAllAgencies = !agencyId;
  const [billingData, setBillingData] = useState<AgencyBillingData | null>(null);
  const [savingsData, setSavingsData] = useState<SavingsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        // If specific agency, load agency billing data
        if (agencyId) {
          const agencyResult = await supabase
            .from('agencies')
            .select('state_billing_rate, caregiver_rate, patient_care_hours, hospital_visit_cost')
            .eq('id', agencyId)
            .single();

          if (agencyResult.error) throw agencyResult.error;
          setBillingData(agencyResult.data);
        } else {
          // For all agencies view, calculate averages from all agencies
          const { data: allAgencies, error } = await supabase
            .from('agencies')
            .select('state_billing_rate, caregiver_rate, patient_care_hours, hospital_visit_cost')
            .eq('is_approved', true);

          if (error) throw error;

          if (allAgencies && allAgencies.length > 0) {
            const agenciesWithRates = allAgencies.filter(a => a.state_billing_rate || a.caregiver_rate);
            if (agenciesWithRates.length > 0) {
              const avgBillingRate = agenciesWithRates.reduce((sum, a) => sum + (a.state_billing_rate || 0), 0) / agenciesWithRates.length;
              const avgCaregiverRate = agenciesWithRates.reduce((sum, a) => sum + (a.caregiver_rate || 0), 0) / agenciesWithRates.length;
              const avgPatientCareHours = agenciesWithRates.reduce((sum, a) => sum + (a.patient_care_hours || 0), 0) / agenciesWithRates.length;
              const avgHospitalVisitCost = agenciesWithRates.reduce((sum, a) => sum + (a.hospital_visit_cost || 2500), 0) / agenciesWithRates.length;
              
              setBillingData({
                state_billing_rate: avgBillingRate,
                caregiver_rate: avgCaregiverRate,
                patient_care_hours: avgPatientCareHours,
                hospital_visit_cost: avgHospitalVisitCost
              });
            }
          }
        }

        // Build query for triage sessions with date filtering
        let sessionsQuery = supabase
          .from('triage_sessions')
          .select(`
            recommendation,
            completed_at,
            patients!inner (
              patient_care_hours_weekly,
              hospital_er_hours
            )
          `)
          .not('completed_at', 'is', null);

        if (agencyId) {
          sessionsQuery = sessionsQuery.eq('agency_id', agencyId);
        }

        if (startDate) {
          sessionsQuery = sessionsQuery.gte('completed_at', startDate.toISOString());
        }
        if (endDate) {
          sessionsQuery = sessionsQuery.lte('completed_at', endDate.toISOString());
        }

        const sessionsResult = await sessionsQuery;

        // Calculate from sessions with patient-level data
        let homeCases = 0, pcpCases = 0, urgentCareCases = 0, erCases = 0;
        let totalErHours = 0;
        
        (sessionsResult.data || []).forEach((s: any) => {
          const erHours = s.patients?.hospital_er_hours || 8;
          switch (s.recommendation) {
            case 'HOME': homeCases++; break;
            case 'PCP': pcpCases++; break;
            case 'URGENT_CARE': urgentCareCases++; break;
            case 'ER_NOW': 
              erCases++; 
              totalErHours += erHours;
              break;
          }
        });

        setSavingsData({
          totalSessions: homeCases + pcpCases + urgentCareCases + erCases,
          homeCases,
          pcpCases,
          urgentCareCases,
          erCases
        });
      } catch (error) {
        console.error('Error loading billing-based savings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [agencyId, startDate, endDate]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Calculate billing-based savings
  const calculateBillingSavings = () => {
    if (!savingsData || !billingData) return null;

    const stateBillingRate = billingData.state_billing_rate || 0;
    const caregiverRate = billingData.caregiver_rate || 0;
    const patientCareHours = billingData.patient_care_hours || 0;
    const hospitalVisitCost = billingData.hospital_visit_cost || 2500;

    // Cases diverted from ER (kept billing active)
    const divertedCases = savingsData.homeCases + savingsData.pcpCases + savingsData.urgentCareCases;
    const erCases = savingsData.erCases;
    
    // Assume average hospital stay disrupts 1 week of billing (5 business days)
    const avgHospitalStayDays = 5;
    const dailyCareHours = patientCareHours / 7; // Weekly hours converted to daily
    
    // Revenue preserved by keeping patients out of hospital
    const revenuePreserved = divertedCases * avgHospitalStayDays * dailyCareHours * stateBillingRate;
    
    // Caregiver wages preserved (work continues when patient not hospitalized)
    const caregiverWagesPreserved = divertedCases * avgHospitalStayDays * dailyCareHours * caregiverRate;
    
    // Net agency margin preserved
    const marginPreserved = revenuePreserved - caregiverWagesPreserved;
    
    // State/Government savings from avoided ER visits
    const governmentSavings = divertedCases * hospitalVisitCost;

    // Loss of Profit to Agency from ER cases
    // When patients go to ER/Hospital, agency loses billing hours and profit margin
    const lossOfProfitToAgency = erCases * avgHospitalStayDays * dailyCareHours * (stateBillingRate - caregiverRate);

    return {
      divertedCases,
      erCases,
      revenuePreserved,
      caregiverWagesPreserved,
      marginPreserved,
      governmentSavings,
      lossOfProfitToAgency,
      stateBillingRate,
      caregiverRate,
      patientCareHours,
      hospitalVisitCost,
      dailyCareHours,
      avgHospitalStayDays
    };
  };

  if (loading) {
    return (
      <Card className={`${chartColors.cardClassName} shadow-sm`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            Billing-Based Savings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const calculations = calculateBillingSavings();
  const hasBillingRates = billingData?.state_billing_rate || billingData?.caregiver_rate;

  if (!hasBillingRates) {
    return (
      <Card className="shadow-sm border-amber-200 bg-amber-50/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-600" />
            Billing-Based Savings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 text-sm text-gray-600">
            <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900 mb-1">Billing rates not configured</p>
              <p>
                Configure State Billing Rate, Caregiver Rate, and Patient Care Hours in the agency profile 
                to see billing-based savings calculations.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!calculations) {
    return null;
  }

  return (
    <TooltipProvider>
      <Card className={`${chartColors.cardClassName} shadow-sm`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600" />
              Billing-Based Savings Analysis
            </span>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="gap-1 text-xs font-normal">
                <Building2 className="h-3 w-3" />
                {isAllAgencies ? 'All Agencies' : agencyName || 'Agency'}
              </Badge>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs" style={chartColors.tooltipStyle}>
                  <p className="text-xs">
                    Calculates savings based on billing hours preserved when patients avoid ER/hospital visits.
                    Assumes {calculations.avgHospitalStayDays}-day average hospital stay disrupting billing.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Main savings figures */}
          <div className="grid grid-cols-2 gap-4">
            <Tooltip>
              <TooltipTrigger className="text-left">
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-green-700">
                    {formatCurrency(calculations.governmentSavings)}
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    State/Medicare Savings
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-white" style={chartColors.tooltipStyle}>
                <p className="text-xs font-medium mb-1 text-gray-900">Government Healthcare Savings</p>
                <p className="text-xs text-gray-600">
                  {calculations.divertedCases.toLocaleString()} diverted cases × {formatCurrency(calculations.hospitalVisitCost)} ER cost = {formatCurrency(calculations.governmentSavings)}
                </p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger className="text-left">
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-blue-700">
                    {formatCurrency(calculations.revenuePreserved)}
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    Revenue Preserved
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-white" style={chartColors.tooltipStyle}>
                <p className="text-xs font-medium mb-1 text-gray-900">Agency Revenue Preserved</p>
                <p className="text-xs text-gray-600">
                  {calculations.divertedCases.toLocaleString()} cases × {calculations.avgHospitalStayDays} days × {calculations.dailyCareHours.toFixed(1)} hrs/day × ${calculations.stateBillingRate}/hr = {formatCurrency(calculations.revenuePreserved)}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Loss of Profit to Agency KPI */}
          {calculations.lossOfProfitToAgency > 0 && (
            <Tooltip>
              <TooltipTrigger className="text-left w-full">
                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-3 border border-red-200">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="text-xl font-bold text-red-700">
                        {formatCurrency(calculations.lossOfProfitToAgency)}
                      </div>
                      <p className="text-xs text-red-600 flex items-center gap-1">
                        <TrendingUp className="h-3 w-3 rotate-180" />
                        Loss of Profit to Agency
                      </p>
                    </div>
                    <div className="text-xs text-gray-600 text-right">
                      <span className="font-medium text-gray-900">{calculations.erCases}</span> Hospital-ER Visits
                    </div>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-white" style={chartColors.tooltipStyle}>
                <p className="text-xs font-medium mb-1 text-gray-900">Loss of Profit Calculation</p>
                <p className="text-xs text-gray-600">
                  {calculations.erCases} ER cases × {calculations.avgHospitalStayDays} days × {calculations.dailyCareHours.toFixed(1)} hrs/day × ${(calculations.stateBillingRate - calculations.caregiverRate).toFixed(2)}/hr margin = {formatCurrency(calculations.lossOfProfitToAgency)}
                </p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Secondary metrics */}
          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-blue-200">
            <Tooltip>
              <TooltipTrigger className="text-left">
                <div className="space-y-0.5">
                  <div className="text-lg font-semibold text-foreground">
                    {formatCurrency(calculations.marginPreserved)}
                  </div>
                  <p className="text-xs text-muted-foreground">Net Margin Preserved</p>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-white" style={chartColors.tooltipStyle}>
                <p className="text-xs text-gray-600">
                  Revenue ({formatCurrency(calculations.revenuePreserved)}) - Caregiver Wages ({formatCurrency(calculations.caregiverWagesPreserved)}) = Net Margin
                </p>
              </TooltipContent>
            </Tooltip>

            <div className="space-y-0.5">
              <div className="text-lg font-semibold text-foreground flex items-center gap-1">
                <Users className="h-4 w-4 text-muted-foreground" />
                {calculations.divertedCases.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">Cases Diverted</p>
            </div>

            <div className="space-y-0.5">
              <div className="text-lg font-semibold text-foreground">
                {(calculations.divertedCases * calculations.avgHospitalStayDays * calculations.dailyCareHours).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              <p className="text-xs text-muted-foreground">Billing Hrs Saved</p>
            </div>
          </div>

          {/* Billing rates info */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Agency Billing Rates</p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground">State Rate:</span>
                <span className="ml-1 font-medium text-foreground">${calculations.stateBillingRate}/hr</span>
              </div>
              <div>
                <span className="text-muted-foreground">Caregiver:</span>
                <span className="ml-1 font-medium text-foreground">${calculations.caregiverRate}/hr</span>
              </div>
              <div>
                <span className="text-muted-foreground">Care Hrs:</span>
                <span className="ml-1 font-medium text-foreground">{calculations.patientCareHours}/wk</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}

